import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '../../i18n'
import { localizedPath } from '../../i18n/routes'
import {
  VOLUME_COLS,
  WEIGHT_ROWS,
  SKU_TIERS,
  volumeColIndex,
  pricePerOrder,
  INTL_LOCALE,
  type PricingContent,
} from '../../data/pricing'

const MIN_ORDERS = VOLUME_COLS[0].min // 100
const MAX_ORDERS = VOLUME_COLS[VOLUME_COLS.length - 1].max // 10 000

/** Échelle logarithmique curseur (0-100) ↔ commandes/mois (100-10 000). */
const RATIO = MAX_ORDERS / MIN_ORDERS

function tToOrders(t: number): number {
  const raw = MIN_ORDERS * Math.pow(RATIO, t / 100)
  // Pas adaptatif : lisible à petit volume, fluide à grand volume.
  const step = raw < 1000 ? 10 : raw < 5000 ? 50 : 100
  return Math.min(MAX_ORDERS, Math.max(MIN_ORDERS, Math.round(raw / step) * step))
}

function ordersToT(orders: number): number {
  return (Math.log(orders / MIN_ORDERS) / Math.log(RATIO)) * 100
}

/** Libellé d'une tranche de poids : « 0 – 1 kg ». */
function weightLabel(row: (typeof WEIGHT_ROWS)[number]): string {
  return `${row.minKg} – ${row.maxKg} kg`
}

type Props = {
  content: PricingContent
  locale: Locale
}

export default function PricingCalculator({ content, locale }: Props) {
  // État initial déterministe (500 commandes, 0-1 kg) : le prix par défaut est
  // rendu en dur dans le HTML prerendu — citable par les crawlers IA sans JS.
  const [t, setT] = useState(() => ordersToT(500))
  const [weightIdx, setWeightIdx] = useState(0)
  const [skuIdx, setSkuIdx] = useState(0)
  // Index === WEIGHT_ROWS.length → « > 20 kg » (sur devis)
  const overweight = weightIdx >= WEIGHT_ROWS.length

  const orders = tToOrders(t)
  const colIdx = volumeColIndex(orders)
  const skuFee = SKU_TIERS[skuIdx].fee
  const price = overweight ? null : pricePerOrder(orders, weightIdx) + skuFee

  const fmtPrice = new Intl.NumberFormat(INTL_LOCALE[locale], {
    style: 'currency',
    currency: 'EUR',
  })
  const fmtInt = new Intl.NumberFormat(INTL_LOCALE[locale])

  // Palier suivant : incitation à la dégressivité.
  const nextCol = colIdx < VOLUME_COLS.length - 1 ? colIdx + 1 : null
  const nextHint =
    !overweight && nextCol !== null
      ? content.calcNextTierHint
          .replace('{orders}', fmtInt.format(VOLUME_COLS[nextCol].min))
          .replace('{price}', fmtPrice.format(WEIGHT_ROWS[weightIdx].prices[nextCol] + skuFee))
      : null

  const contactPath = localizedPath('contact', locale)
  /** Quote tab — this calculator's output IS the start of a quote. */
  const quotePath = `${contactPath}#devis`

  return (
    /* Plateau Double-Bezel à DEUX plaques (entrées blanches / résultat nuit),
       séparées par le gap du plateau — lecture « instrument ». Variante NUIT :
       le plateau est en verre sombre, la page /tarifs le sertit dans son
       hero scène nuit. */
    <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-1 sm:gap-1.5 rounded-[1.75rem] bg-white/[0.06] p-1 sm:p-1.5 ring-1 ring-white/10">
        {/* ───────────── Inputs ───────────── */}
        <div className="rounded-[calc(1.75rem-0.375rem)] border border-ink-950/[0.05] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-5 sm:p-8 lg:p-10">
          {/* Commandes / mois */}
          <label htmlFor="calc-orders" className="block text-sm font-semibold text-ink-950">
            {content.calcOrdersLabel}
          </label>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-semibold tracking-[-0.03em] tabular-nums text-[clamp(2rem,4vw,2.8rem)] leading-none bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
              {fmtInt.format(orders)}
            </span>
            <span className="text-sm text-ink-500">{content.calcOrdersUnit}</span>
          </div>
          <input
            id="calc-orders"
            type="range"
            min={0}
            max={100}
            step={0.5}
            value={t}
            onChange={(e) => setT(Number(e.target.value))}
            className="mt-4 w-full h-2 cursor-pointer accent-accent-600"
            aria-valuetext={`${fmtInt.format(orders)} ${content.calcOrdersUnit}`}
          />
          <div className="mt-1.5 flex justify-between text-[11px] text-ink-400 tabular-nums">
            <span>{fmtInt.format(MIN_ORDERS)}</span>
            <span>{fmtInt.format(MAX_ORDERS)}</span>
          </div>

          {/* Poids moyen */}
          <p className="mt-8 text-sm font-semibold text-ink-950">{content.calcWeightLabel}</p>
          <div className="mt-3 grid grid-cols-3 gap-2" role="group" aria-label={content.calcWeightLabel}>
            {WEIGHT_ROWS.map((row, i) => (
              <button
                key={row.maxKg}
                type="button"
                aria-pressed={weightIdx === i}
                onClick={() => setWeightIdx(i)}
                className={`rounded-xl border px-2 sm:px-3 py-2.5 text-sm whitespace-nowrap font-medium tabular-nums transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500 ${
                  weightIdx === i
                    ? 'border-accent-600 bg-accent-600 text-white shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]'
                    : 'border-ink-950/10 bg-white text-ink-700 hover:border-accent-300 hover:bg-accent-50'
                }`}
              >
                {weightLabel(row)}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={overweight}
              onClick={() => setWeightIdx(WEIGHT_ROWS.length)}
              className={`rounded-xl border px-2 sm:px-3 py-2.5 text-sm whitespace-nowrap font-medium tabular-nums transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500 ${
                overweight
                  ? 'border-ink-950 bg-ink-950 text-white'
                  : 'border-ink-950/10 bg-white text-ink-700 hover:border-accent-300 hover:bg-accent-50'
              }`}
            >
              {content.calcOverWeightLabel}
            </button>
          </div>

          {/* Nombre de SKU */}
          <p className="mt-8 text-sm font-semibold text-ink-950">{content.calcSkuLabel}</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" role="group" aria-label={content.calcSkuLabel}>
            {SKU_TIERS.map((tier, i) => (
              <button
                key={tier.label}
                type="button"
                aria-pressed={skuIdx === i}
                onClick={() => setSkuIdx(i)}
                className={`rounded-xl border px-3 py-2.5 text-sm font-medium tabular-nums transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500 ${
                  skuIdx === i
                    ? 'border-accent-600 bg-accent-600 text-white shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]'
                    : 'border-ink-950/10 bg-white text-ink-700 hover:border-accent-300 hover:bg-accent-50'
                }`}
              >
                {tier.label} SKU
              </button>
            ))}
          </div>

          {/* Porte de sortie < 100 commandes */}
          <p className="mt-8 text-xs text-ink-500">
            {content.calcUnderMin}{' '}
            <Link
              to={contactPath}
              className="font-medium text-accent-700 hover:text-accent-800 underline underline-offset-2 transition-colors"
            >
              {content.calcUnderMinCta}
            </Link>
          </p>
        </div>

        {/* ───────────── Résultat ─────────────
            flex flex-col + flex-1 sur le contenu : le CTA reste dans le cadre
            quelle que soit la hauteur (le h-full posait un overflow). */}
        <div className="relative rounded-[calc(1.75rem-0.375rem)] ring-1 ring-white/10 bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white p-5 sm:p-8 lg:p-10 overflow-hidden isolate flex flex-col">
          <div
            aria-hidden
            className="absolute inset-0 opacity-25 -z-10"
            style={{
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
              maskImage: 'radial-gradient(ellipse at top right, black 20%, transparent 75%)',
            }}
          />
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.18, 1], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -right-24 -top-24 w-[320px] h-[320px] rounded-full blur-3xl -z-10"
            style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.5), transparent 70%)' }}
          />

          <AnimatePresence mode="wait" initial={false}>
            {overweight ? (
              <motion.div
                key="quote"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col flex-1"
              >
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-300">
                  {content.calcResultLabel}
                </p>
                <h3 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight">
                  {content.calcQuoteTitle}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-ink-300 leading-relaxed">
                  {content.calcQuoteText}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="price"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col flex-1"
              >
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-300">
                  {content.calcResultLabel}
                </p>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-semibold tracking-[-0.04em] tabular-nums leading-none text-[clamp(2.6rem,5vw,3.8rem)] bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent">
                    {price !== null ? fmtPrice.format(price) : ''}
                  </span>
                  <span className="text-sm text-ink-400">{content.calcResultUnit}</span>
                </div>
                <p className="mt-3 text-xs sm:text-sm text-ink-400 leading-relaxed">
                  {content.calcResultIncludes}
                </p>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-400">
                    {content.calcMonthlyLabel}
                  </p>
                  <p className="mt-1 text-xl sm:text-2xl font-semibold tabular-nums tracking-tight">
                    {price !== null ? fmtPrice.format(price * orders) : ''}
                    <span className="ml-1.5 text-sm font-normal text-ink-400">
                      {content.calcMonthlyUnit}
                    </span>
                  </p>
                </div>

                {nextHint && (
                  <p className="mt-4 inline-flex items-start gap-2 text-xs text-accent-200/90 leading-relaxed">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      className="mt-0.5 shrink-0"
                    >
                      <path d="M13 17l5-5-5-5M6 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {nextHint}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <Link
            to={quotePath}
            className="relative group mt-8 inline-flex w-full items-center justify-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full px-6 py-3.5 text-sm font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
          >
            <span
              aria-hidden
              className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
            />
            <span className="relative">{content.calcCta}</span>
            <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-ink-950 text-white">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Link>
        </div>
    </div>
  )
}
